import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Pause, ExternalLink, Music, Mic, User, Ticket, Radio, Download, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

/**
 * ArtistPage — public artist profile with a streamable track catalog.
 * Route: /artists/:aid
 */
export default function ArtistPage() {
  const { aid } = useParams();
  const { user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [merch, setMerch] = useState([]);
  const [liveSession, setLiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nowId, setNowId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/artists/${aid}`);
        setArtist(data.artist);
        setTracks(data.tracks || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
      // Fire the merch fetch in parallel — best-effort, non-blocking.
      api.get(`/artists/${aid}/merch`)
        .then(({ data }) => setMerch(data?.items || []))
        .catch(() => setMerch([]));
      // Look for a currently-live band session on this artist page. Loops
      // through every open session (a small cheap request) and matches by
      // artist_id. Renders the "Live now" badge + jump-to-stream card.
      api.get("/marketplace/live/sessions", { params: { status: "live", limit: 50 } })
        .then(({ data }) => {
          const s = (data?.items || []).find((it) => it.artist_id === aid);
          setLiveSession(s || null);
        })
        .catch(() => setLiveSession(null));
    })();
  }, [aid]);

  const play = async (t) => {
    if (!user) {
      toast.error("Sign in to listen");
      return;
    }
    try {
      const { data } = await api.get(`/playlists/tracks/${t.id}/stream-url`);
      if (audioRef.current) {
        audioRef.current.src = data.stream_url;
        audioRef.current.play();
        setNowId(t.id);
        setPlaying(true);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Digital download purchase — kicks off Stripe Checkout and redirects.
  const buyTrack = async (t) => {
    if (!user) { toast.error("Sign in to buy"); return; }
    try {
      const { data } = await api.post(`/playlists/tracks/${t.id}/buy`, {
        origin_url: window.location.origin,
      });
      if (data.already_purchased) {
        toast.success("You already own this track — heading to your downloads.");
        window.location.href = "/my/downloads";
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadTrack = async (t) => {
    try {
      const { data } = await api.get(`/playlists/tracks/${t.id}/download-url`);
      // Force-download in a new tab using the presigned URL.
      const a = document.createElement("a");
      a.href = data.download_url;
      a.download = data.filename || "track.mp3";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-[#8B7B73]">Loading…</div>;
  if (!artist) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <Mic size={30} className="mx-auto text-[#B31E1E] mb-3" />
        <h1 className="font-black text-2xl text-[#1B1614]">Artist not found</h1>
        <Link to="/directory" className="mt-4 inline-block text-[#B31E1E] hover:underline">← Back to directory</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 pb-32" data-testid="artist-page">
      {/* Hero cover image — when set, renders wide above the profile card */}
      {artist.cover_image_url && (
        <div
          className="relative rounded-2xl overflow-hidden border border-[#EAE3DE] mb-6 aspect-[16/7] md:aspect-[16/6] bg-[#1B1614]"
          data-testid="artist-cover-image"
        >
          <img
            src={artist.cover_image_url}
            alt={`${artist.stage_name} cover`}
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* Subtle bottom-fade so the stage name in the next card doesn't feel disconnected */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        </div>
      )}

      {/* Hero */}
      <div className="rounded-2xl border border-[#EAE3DE] bg-white p-8 flex items-start gap-6 flex-wrap">
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#B31E1E] to-[#8F1717] flex items-center justify-center shrink-0">
          <Mic size={40} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
            <Mic size={11} /> Servrevu artist
          </div>
          <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">
            {artist.stage_name}
          </h1>
          {/* Owner-only actions — visible only to the logged-in band owner */}
          {user && artist.user_id === user.id && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="artist-owner-actions">
              <Link to={`/marketplace/live/new?artist_id=${artist.id}`}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-[12px] font-semibold shadow-sm" data-testid="artist-go-live-cta">
                  <Radio size={12} /> Go live
                </span>
              </Link>
              <Link to="/concerts/new">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B31E1E] hover:bg-[#8F1717] text-white px-3 py-1.5 text-[12px] font-semibold shadow-sm">
                  <Ticket size={12} /> Create event
                </span>
              </Link>
              <Link to="/audio/library" className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE3DE] bg-white px-3 py-1.5 text-[12px] text-[#1B1614] hover:border-[#B31E1E]">
                + Upload track
              </Link>
            </div>
          )}
          {/* Public-facing "Live now" strip when this band is broadcasting */}
          {liveSession && (
            <Link
              to={`/marketplace/live/${liveSession.id}`}
              className="mt-3 group inline-flex items-center gap-3 rounded-md border-2 border-red-500 bg-red-50 hover:bg-red-100 px-4 py-2.5 transition-colors"
              data-testid="artist-live-now-strip"
            >
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider font-mono text-red-600 font-bold">Live now · {liveSession.viewer_count || 0} watching</p>
                <p className="text-sm font-semibold text-[#1B1614] truncate">{liveSession.title}</p>
              </div>
              <span className="ml-auto text-xs font-semibold text-red-600 group-hover:underline shrink-0">Watch →</span>
            </Link>
          )}
          {artist.bio && (
            <p className="mt-3 text-[#5C4E48] max-w-2xl leading-relaxed" data-testid="artist-bio-text">
              {artist.bio}
            </p>
          )}
          {artist.website && (
            <a
              href={artist.website}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#B31E1E] hover:underline"
              data-testid="artist-website-link"
            >
              <ExternalLink size={13} /> {artist.website}
            </a>
          )}
          {/* External-platform links */}
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {artist.apple_music_url && (
              <a
                href={artist.apple_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-[#EAE3DE] bg-white px-3 py-1.5 hover:border-[#B31E1E] text-[#1B1614]"
                data-testid="artist-apple-music-link"
              >
                🎵 Apple Music
              </a>
            )}
            {artist.bandcamp_url && (
              <a
                href={artist.bandcamp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-[#EAE3DE] bg-white px-3 py-1.5 hover:border-[#B31E1E] text-[#1B1614]"
                data-testid="artist-bandcamp-link"
              >
                🎧 Bandcamp
              </a>
            )}
            {artist.spotify_url && (
              <a
                href={artist.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#1DB954] text-white px-3 py-1.5 hover:bg-[#1AA34A] font-semibold"
                data-testid="artist-spotify-link"
              >
                Listen on Spotify
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Spotify embed (if provided) — official iframe from Spotify. */}
      {artist.spotify_kind && artist.spotify_id && (
        <div className="mt-6 rounded-2xl border border-[#EAE3DE] bg-white overflow-hidden" data-testid="artist-spotify-embed">
          <div className="px-5 pt-4 pb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48]">
              On Spotify
            </span>
            <a
              href={artist.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#B31E1E] hover:underline inline-flex items-center gap-1"
            >
              Open <ExternalLink size={11} />
            </a>
          </div>
          <iframe
            title={`${artist.stage_name} on Spotify`}
            src={`https://open.spotify.com/embed/${artist.spotify_kind}/${artist.spotify_id}?utm_source=servrevu`}
            width="100%"
            height={artist.spotify_kind === "artist" ? 380 : 352}
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            style={{ border: 0 }}
          />
        </div>
      )}

      {/* Merch — marketplace listings the artist has attached to this
          profile (band tees, vinyl, prints, etc.). Only rendered when there
          are active merch items. */}
      {merch.length > 0 && (
        <div className="mt-8" data-testid="artist-merch-strip">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48]">
                Merch ({merch.length})
              </h2>
              <p className="mt-0.5 text-xs text-[#8B7B73]">Buy direct from the artist.</p>
            </div>
            <Link
              to={`/marketplace?artist_id=${aid}`}
              className="text-xs text-[#B31E1E] font-semibold hover:underline underline-offset-4"
              data-testid="artist-merch-see-all"
            >
              See all →
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [scrollbar-width:none]" data-testid="artist-merch-scroller">
            {merch.map((m) => (
              <Link
                key={m.id}
                to={`/marketplace/listings/${m.id}`}
                className="group shrink-0 w-40 snap-start rounded-xl overflow-hidden border border-[#EAE3DE] bg-white hover:border-[#B31E1E] hover:shadow-[0_10px_30px_-14px_rgba(179,30,30,0.4)] transition-all"
                data-testid={`artist-merch-item-${m.id}`}
              >
                <div className="aspect-square bg-[#FAF5F2] relative overflow-hidden">
                  {m.photos?.[0] ? (
                    <img src={m.photos[0]} alt={m.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#8B7B73]"><Music size={22} /></div>
                  )}
                  {m.listing_type === "auction" && (
                    <span className="absolute top-1.5 right-1.5 rounded bg-[#B31E1E] text-white text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5">Auction</span>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[13px] font-semibold text-[#1B1614] truncate">
                    ${((m.listing_type === "auction" ? (m.current_bid_cents || m.starting_bid_cents) : m.price_cents) / 100).toFixed(2)}
                  </div>
                  <div className="text-[11px] text-[#5C4E48] truncate">{m.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Track list */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48]">
            Tracks ({tracks.length})
          </h2>
        </div>
        {tracks.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-[#EAE3DE] p-10 text-center bg-white">
            <Music size={30} className="mx-auto text-[#B31E1E] mb-3" />
            <p className="text-[#1B1614] font-semibold">No public tracks yet</p>
            <p className="text-sm text-[#5C4E48] mt-1">Check back soon.</p>
          </Card>
        ) : (
          <ul className="space-y-1.5" data-testid="artist-tracks">
            {tracks.map((t, i) => {
              const isCurrent = nowId === t.id;
              return (
                <li
                  key={t.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                    isCurrent ? "border-[#B31E1E]/40 bg-[#F9E4E1]/40" : "border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
                  } transition-colors`}
                  data-testid={`artist-track-${t.id}`}
                >
                  <span className="w-6 text-center text-xs font-mono text-[#8B7B73] shrink-0">{i + 1}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full h-9 w-9 p-0 hover:bg-[#B31E1E] hover:text-white"
                    onClick={() => (isCurrent && playing ? audioRef.current?.pause() : play(t))}
                    data-testid={`artist-track-play-${t.id}`}
                  >
                    {isCurrent && playing ? <Pause size={16} /> : <Play size={16} />}
                  </Button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1B1614] truncate">{t.title}</div>
                    <div className="text-xs text-[#8B7B73] truncate">
                      {t.album || artist.stage_name}
                      {t.duration_seconds ? ` · ${formatDuration(t.duration_seconds)}` : ""}
                    </div>
                  </div>
                  <span className="text-xs font-mono text-[#8B7B73] hidden sm:inline">
                    {t.play_count} plays
                  </span>
                  {/* Digital download — Buy for non-owners, direct Download for
                      owners (buyers can hit /my/downloads to fetch). */}
                  {t.for_sale && (
                    t.is_mine ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-8 text-xs"
                        onClick={() => downloadTrack(t)}
                        data-testid={`artist-track-download-${t.id}`}
                      >
                        <Download size={12} className="mr-1" /> Download
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="rounded-sm h-8 text-xs bg-[#B31E1E] hover:bg-[#8F1717] text-white"
                        onClick={() => buyTrack(t)}
                        data-testid={`artist-track-buy-${t.id}`}
                      >
                        <ShoppingCart size={12} className="mr-1" /> ${(t.price_cents / 100).toFixed(2)}
                      </Button>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Persistent audio element */}
      <audio
        ref={audioRef}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="hidden"
      />

      {nowId && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#EAE3DE]" data-testid="artist-player-bar">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-4">
            <User size={16} className="text-[#B31E1E]" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1B1614] truncate">
                {tracks.find((t) => t.id === nowId)?.title}
              </div>
              <div className="text-xs text-[#8B7B73] truncate">{artist.stage_name}</div>
            </div>
            <Button
              size="sm"
              className="rounded-full h-9 w-9 p-0 bg-[#B31E1E] hover:bg-[#8F1717] text-white"
              onClick={() => (playing ? audioRef.current?.pause() : audioRef.current?.play())}
              data-testid="artist-player-toggle"
            >
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(s) {
  const total = Math.max(0, Math.floor(s));
  const m = Math.floor(total / 60);
  const sec = String(total % 60).padStart(2, "0");
  return `${m}:${sec}`;
}
